import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  OutlinedInput,
  Typography,
} from "@mui/material";
import ModalBase from "./ModalBase";
import { useContext, useEffect, useState } from "react";
import { SimpleFormInput } from "../forms/SimpleFormInput";
import ConfirmChangesButtons from "../configuration/ConfirmChangesButtons";
import {
  validateFallbackQuotes,
} from "../../utils/credentials";
import { UserContext } from "../../contexts/userContext";
import {
  COUNTRIES_WITH_GEOGRAPHIC_TYPE,
  DEFAULT_GEOGRAPHIC_TYPE,
} from "../../utils/constants";
import {
  IFallbackWeightRangeProps,
  IOwnLogisticsCredentials,
  IOwnLogisticsRulesModal,
  WeightRange,
} from "../../interfaces/configuration";

const FallbackWeightRange = ({
  weight,
  rangeLabel,
  feeValue,
  feeErrors,
  onChange,
  showErrors,
}: IFallbackWeightRangeProps) => {
  const isLeftColumn = weight === 50 || weight === 200;
  const isRightColumn = weight === 120 || weight === 250;

  return (
    <Grid
      item
      xs={6}
      paddingRight={isLeftColumn ? 1 : 0}
      paddingLeft={isRightColumn ? 1 : 0}
      className="m-b-20"
    >
      <Typography className="f-s-14 f-w-600 m-b-10">{rangeLabel}</Typography>
      <FormControl size="small" fullWidth>
        <OutlinedInput
          type="number"
          placeholder="Costo"
          value={feeValue ?? ""}
          startAdornment="$"
          onChange={(e) => onChange(weight, e.target.value)}
          error={feeErrors !== undefined && feeErrors.length > 0}
          className="f-s-14"
          inputProps={{ step: "0.01", min: "0" }}
          sx={{ backgroundColor: "white" }}
        />
        {feeErrors ? showErrors(feeErrors) : null}
      </FormControl>
    </Grid>
  );
};

const WEIGHT_RANGE_LABELS: Record<WeightRange, string> = {
  50: "Costo (entre 0.1kg y 50kg)",
  120: "Costo (entre 50.1kg y 120kg)",
  200: "Costo (entre 120.1kg y 200kg)",
  250: "Costo (entre 200.1kg y 250kg)",
};

const WEIGHT_RANGES: readonly WeightRange[] = [50, 120, 200, 250] as const;

const getDefaultFallbackQuotation = () => ({
  ranges: {
    50: {},
    120: {},
    200: {},
    250: {},
  },
});

const showInputErrors = (inputErrors: string[]) =>
  inputErrors.map((message: string) => (
    <FormHelperText className="color-error" key={message}>
      {message}
    </FormHelperText>
  ));

// eslint-disable-next-line max-lines-per-function
const OwnFleetRulesModal = ({
  open,
  onClose,
  credentials,
  setCredentials,
}: IOwnLogisticsRulesModal) => {
  const { currentUser } = useContext(UserContext);
  const country = currentUser?.entityInfo?.country ?? "";
  const defaultGeoType = COUNTRIES_WITH_GEOGRAPHIC_TYPE.includes(country)
    ? DEFAULT_GEOGRAPHIC_TYPE
    : undefined;

  const [credentialChanges, setCredentialChanges] =
    useState<IOwnLogisticsCredentials>({
      ...credentials,
      geographic_type: defaultGeoType,
    });
  const [fallbackErrors, setFallbackErrors] = useState<{
    [key: number]: { fee?: string[] };
  }>({});

  useEffect(() => {
    setCredentialChanges({
      ...credentials,
      geographic_type: defaultGeoType,
    });
  }, [credentials, defaultGeoType]);

  const handleFallbackChange = (weight: WeightRange, value: string) => {
    const numValue = value === "" ? undefined : Number(value);
    const fallbackRanges = credentialChanges?.fallback_quotation?.ranges;
    setCredentialChanges({
      ...credentialChanges,
      fallback_quotation: {
        ranges: {
          ...(fallbackRanges || getDefaultFallbackQuotation().ranges),
          [weight]: {
            ...(fallbackRanges?.[weight] || {}),
            fee: numValue,
          },
        },
      },
    });
    setFallbackErrors({ ...fallbackErrors, [weight]: {} });
  };

  const handleConfirmRules = () => {
    const fallbackCurrentErrors = credentialChanges?.fallback_quotation
      ? validateFallbackQuotes(credentialChanges.fallback_quotation)
      : {};

    if (Object.keys(fallbackCurrentErrors).length > 0) {
      setFallbackErrors(fallbackCurrentErrors);
      return;
    }

    setFallbackErrors({});
    setCredentials(credentialChanges);
    onClose();
  };

  const handleCancel = () => {
    onClose();
    setTimeout(() => {
      setCredentialChanges({
        ...credentials,
        geographic_type: defaultGeoType,
      });
    }, 500);
  };
  return (
    <ModalBase open={open} handleClose={onClose}>
      <Grid container paddingTop={3}>
        <Typography className="f-s-24 m-b-20">Reglas</Typography>

        <Box display={"flex"} alignItems={"center"} className="m-b-20">
          <IconButton size="small" color="primary">
            <span className="material-symbols-rounded">info</span>
          </IconButton>
          <Typography className="f-s-14">
            Configura tu matriz logística con límites de peso o precio. Estos
            definirán la cotización máxima que verá el usuario en el checkout.
          </Typography>
        </Box>

        <Grid item xs={6} paddingRight={1}>
          <SimpleFormInput
            inputTitle="Ingresar límite de peso en kg"
            valueName="maximum_weight"
            values={credentialChanges || 0}
            errors={[]}
            setValues={setCredentialChanges}
            endAdornment="Kg"
            inputProps={{
              type: "number",
              min: 0,
              style: {
                borderRight: "1px solid #c5c7cf",
                margin: "0.6rem 0.6rem 0.6rem 0",
                boxSizing: "border-box",
                paddingBlock: 0,
              },
            }}
          />
        </Grid>
        <Grid item xs={6} paddingLeft={1}>
          <SimpleFormInput
            inputTitle="Ingresar límite de precio"
            valueName="maximum_price"
            values={credentialChanges || 0}
            errors={[]}
            setValues={setCredentialChanges}
            startAdornment="$"
            inputProps={{
              type: "number",
              min: 0,
              style: {
                paddingBottom: "9px",
              },
            }}
          />
        </Grid>
        <Box display={"flex"} alignItems={"center"} className="m-t-20 m-b-20">
          <IconButton size="small" color="primary">
            <span className="material-symbols-rounded">info</span>
          </IconButton>
          <Typography className="f-s-14">
            Configura la cotización de respaldo. Los costos por rango de peso
            definidos se aplicarán cuando no se pueda realizar la cotización
            regular.
          </Typography>
        </Box>

        <Grid container>
          {WEIGHT_RANGES.map((weight) => (
            <FallbackWeightRange
              key={weight}
              weight={weight}
              rangeLabel={WEIGHT_RANGE_LABELS[weight]}
              feeValue={
                credentialChanges?.fallback_quotation?.ranges[weight].fee
              }
              feeErrors={fallbackErrors[weight]?.fee as string[] | undefined}
              onChange={handleFallbackChange}
              showErrors={showInputErrors}
            />
          ))}
        </Grid>

        <ConfirmChangesButtons
          cancelButtonText="Volver"
          handleConfirm={handleConfirmRules}
          handleCancel={handleCancel}
          size={6}
        />
      </Grid>
    </ModalBase>
  );
};

export default OwnFleetRulesModal;
